import { useEffect, useState } from "react";
import { ChevronDown, HelpCircle, ShieldCheck, Database, AlertTriangle, Zap, CheckCircle2, Sparkles } from "lucide-react";

interface FAQ {
  q: string;
  a: string;
}

interface FAQGroup {
  category: string;
  icon: any;
  color: string;
  faqs: FAQ[];
}

const FAQS: FAQGroup[] = [
  {
    category: "Sobre o Funcionamento",
    icon: Zap,
    color: "text-blue-500",
    faqs: [
      {
        q: "Como o DataGrid XML processa os arquivos XML?",
        a: "A plataforma extrai dados estruturados utilizando algoritmos de parsing e data science em background no seu próprio navegador. Não fazemos o upload do arquivo XML original para o servidor, garantindo máxima eficiência."
      },
      {
        q: "Posso importar arquivos XML agrupados em pastas ZIP?",
        a: "Sim, nossa engine suporta a descompactação automática de arquivos ZIP contendo centenas ou até dezenas de milhares de notas fiscais, indexando-as instantaneamente."
      },
      {
        q: "Qual é o limite de XMLs que posso processar de uma vez?",
        a: "Depende apenas da capacidade de memória do seu computador. Como o processamento ocorre via Edge Computing no seu navegador, recomendamos lotes de até 50.000 arquivos para uma fluidez ideal de interface."
      },
      {
        q: "Preciso de conexão constante com a internet?",
        a: "Não. Uma vez que o dashboard é carregado na tela, todo o core de ingestão e processamento estatístico dos XMLs ocorre de forma integralmente offline na sua máquina."
      },
      {
        q: "Os relatórios gerados podem ser exportados?",
        a: "Sim. A plataforma permite exportações dinâmicas em lote padronizadas em CSV (compatíveis com Excel e Power BI) ou em formatos PDF para relatórios executivos."
      }
    ]
  },
  {
    category: "Segurança e Privacidade",
    icon: ShieldCheck,
    color: "text-emerald-500",
    faqs: [
      {
        q: "Meus dados fiscais ficam armazenados na nuvem?",
        a: "Não. Os XMLs brutos e sigilosos não ficam expostos em nosso banco de dados. Utilizamos a arquitetura de banco de dados em memória (IndexedDB) local para assegurar a custódia das informações na sua máquina."
      },
      {
        q: "O sistema respeita as diretrizes da LGPD?",
        a: "Sim, por design (Privacy by Design). A retenção da volumetria ocorre localmente e dispomos de recursos como o Modo Privacidade, que limita drasticamente a exposição não autorizada de dados de terceiros."
      },
      {
        q: "O que é o botão \"Modo de Privacidade DLP\"?",
        a: "É o botão com formato de 'Olho' no topo do site. Trata-se de um recurso de ofuscação (Data Loss Prevention) que borra CPFs, CNPJs, nomes e valores financeiros em toda a plataforma. Ideal para reuniões ou compartilhamento de tela."
      },
      {
        q: "E se eu quiser apagar todo o meu histórico de dados?",
        a: "Basta utilizar a tela de \"Sincronização Offline\" no menu e clicar em \"Purgar Dados\", ou simplesmente realizar o Log Out. Todo o cache será instantaneamente destruído do navegador."
      },
      {
        q: "Quem tem acesso às métricas e chaves processadas?",
        a: "Apenas metadados de telemetria estatística do sistema podem ser persistidos para manter o seu perfil operante na nuvem, mas as tags sensíveis do XML jamais saem do seu hardware sem seu comando de exportação expresso."
      }
    ]
  },
  {
    category: "Propósito (Data Science & TI)",
    icon: Database,
    color: "text-amber-500",
    faqs: [
      {
        q: "O DataGrid XML é um software de gestão fiscal/tributária?",
        a: "Não. Somos uma ferramenta exclusiva do segmento de Tecnologia da Informação (TI) e Data Science, focada unicamente na raspagem, transformação, higienização e modelagem matemática de dados massivos em formato XML."
      },
      {
        q: "O que a Inteligência Artificial faz na plataforma?",
        a: "Nossos algoritmos investigam correlações matemáticas invisíveis a olho nu, detectam anomalias nas strings de preenchimento e agrupam famílias de produtos (NCMs) usando clusters estatísticos baseados na imensa massa de dados que você fornecer."
      },
      {
        q: "Quem é o público ideal desta plataforma?",
        a: "Analistas de dados, desenvolvedores, engenheiros de BI e profissionais de gestão corporativa que precisam destrinchar enormes bancos de XMLs para alimentar Data Lakes, Dashboards ou sistemas ERP."
      },
      {
        q: "A plataforma gera ou envia guias de impostos?",
        a: "Em hipótese alguma. Não possuímos atributos ou integrações contábeis com a SEFAZ para a geração de guias ou tributos. Focamos estritamente na Ciência de Dados."
      },
      {
        q: "Vocês fornecem consultoria tributária em cima dos resultados?",
        a: "Não. Nós operamos apenas como a ferramenta de Big Data para extração. A interpretação humana da lei e o direcionamento consultivo devem ser obtidos exclusivamente com o seu corpo jurídico ou especialistas regulatórios habilitados."
      }
    ]
  },
  {
    category: "O que a ferramenta NÃO é (Disclaimer Técnico)",
    icon: AlertTriangle,
    color: "text-red-500",
    faqs: [
      {
        q: "Este sistema realiza atividade privativa da profissão de contador?",
        a: "Absolutamente não. A plataforma se recusa a atuar na estruturação de balanços, apurações tributárias declaratórias ou elaboração de obrigações acessórias, não exercendo qualquer ato da jurisdição privativa de conselhos regulamentadores externos."
      },
      {
        q: "O Extrator realiza 'recuperação tributária' para minha empresa?",
        a: "Não. A ferramenta não submete compensações e não atua em esferas legais ou de reparação. Apenas inspecionamos matematicamente a integridade de dados que você já possui nos XMLs."
      },
      {
        q: "A plataforma atua como um sistema de 'Análise de Conformidade' oficial?",
        a: "A plataforma faz a \"Inspeção de Regras e Validação Estrutural Algorítmica\" (identificação de hashes inválidos ou diferenças matemáticas brutas). Nós não somos uma entidade de fiscalização ou contabilidade e o relatório extraído não serve como atestado ou laudo legal perante o fisco."
      },
      {
        q: "Posso substituir o trabalho do meu escritório de consultoria pelo site?",
        a: "Nunca. O Extrator é apenas um software acelerador que lê dados pesados. Ele pode inclusive ser útil para o seu próprio consultor especializado, mas de forma alguma substitui as responsabilidades técnicas, a análise interpretativa e as obrigações legais assumidas por um profissional devidamente credenciado."
      },
      {
        q: "O que significam as 'Divergências Fiscais' mostradas na plataforma?",
        a: "Elas significam estritamente uma assimetria matemática detectada no código do arquivo XML (Exemplo: o valor declarado na Tag X difere do valor da Tag Y multiplicado pela Tag Z). Isso é um fato matemático a ser encaminhado ao seu analista responsável para interpretação qualitativa."
      }
    ]
  },
  {
    category: "Funcionalidades Avançadas",
    icon: Sparkles,
    color: "text-purple-500",
    faqs: [
      {
        q: "Como funciona o seletor de Entidade de Inspeção no cabeçalho?",
        a: "O seletor permite isolar toda a análise do site para um CNPJ específico. Ao selecionar uma empresa, o sistema recalibra instantaneamente todos os gráficos, KPIs e tabelas, garantindo que você visualize apenas os dados daquela entidade, sem contaminação de outros lotes."
      },
      {
        q: "O que é o módulo de 'Faturas e Duplicatas'?",
        a: "Este módulo extrai automaticamente os dados de cobrança contidos nos XMLs (parcelas, vencimentos e valores). Ele organiza o fluxo de caixa projetado, permitindo visualizar compromissos a pagar e receber com base estritamente nos documentos fiscais."
      },
      {
        q: "O que encontro no módulo de 'Logística & Transporte'?",
        a: "Uma visão técnica da operação de frete. O sistema identifica transportadoras, placas de veículos, modalidades de frete (CIF/FOB) e totaliza volumes e pesos (bruto/líquido) para auditoria de custos logísticos."
      },
      {
        q: "Como o sistema lida com múltiplos lotes da mesma empresa?",
        a: "O sistema normaliza os CNPJs e agrupa automaticamente todos os lotes de importação da mesma entidade sob uma única opção no seletor, permitindo uma visão consolidada de toda a história fiscal daquela empresa."
      },
      {
        q: "Os dados persistidos no banco local são seguros?",
        a: "Sim. Além de ficarem restritos ao seu navegador, os dados sensíveis são armazenados com criptografia AES-GCM High. O acesso é isolado por perfil de usuário, garantindo que um auditor não veja os dados de outro, mesmo no mesmo hardware."
      }
    ]
  }
];

function FAQItem({ faq }: { faq: FAQ }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`glass-card overflow-hidden transition-all duration-300 ${open ? 'border-primary/40 shadow-md' : 'border-border/40 hover:border-border'}`}>
      <button 
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 focus:outline-none"
      >
        <span className="font-bold text-foreground text-[15px] pr-8">{faq.q}</span>
        <div className={`shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
          <ChevronDown className="size-5 text-muted-foreground" />
        </div>
      </button>
      <div 
        className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <p className="text-muted-foreground text-sm leading-relaxed border-l-2 border-primary/30 pl-4 py-1">
          {faq.a}
        </p>
      </div>
    </div>
  );
}

export default function AjudaPage() {
  useEffect(() => { document.title = "Central de Ajuda — DataGrid XML"; }, []);

  return (
    <div className="px-6 py-8 md:px-8 max-w-[1200px] mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
      
      {/* Header Central de Ajuda */}
      <header className="glass-card rounded-3xl p-8 lg:p-12 border-t-4 border-t-primary relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="relative z-10 space-y-4 max-w-2xl text-center md:text-left">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-2">
            <HelpCircle className="size-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-foreground">
            Central de Ajuda
          </h1>
          <p className="text-base md:text-lg text-muted-foreground font-medium leading-relaxed">
            Esclareça todas as suas dúvidas sobre arquitetura técnica, modelo de processamento estatístico, conformidade ética e a destinação de Data Science da nossa tecnologia.
          </p>
        </div>
        
        {/* Decorative Graphic */}
        <div className="relative shrink-0 hidden lg:block">
          <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
          <div className="relative size-48 rounded-full border border-primary/20 bg-background/50 backdrop-blur-xl flex items-center justify-center shadow-2xl">
            <div className="size-32 rounded-full border border-primary/30 bg-primary/5 flex items-center justify-center">
              <div className="size-16 rounded-full bg-primary flex items-center justify-center shadow-[0_0_40px_rgba(var(--primary),0.5)]">
                <CheckCircle2 className="size-8 text-primary-foreground" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Accordions por Categoria */}
      <div className="space-y-12 pb-20">
        {FAQS.map((group, index) => (
          <section 
            key={group.category} 
            className="animate-in-card" 
            style={{ animationDelay: `${(index + 1) * 150}ms` }}
          >
            <div className="flex items-center gap-3 mb-6 px-2">
              <div className={`p-2.5 rounded-xl bg-background/50 glass border border-border shadow-sm`}>
                <group.icon className={`size-6 ${group.color}`} />
              </div>
              <h2 className="text-2xl font-black tracking-tight text-foreground">
                {group.category}
              </h2>
            </div>
            
            <div className="grid gap-3">
              {group.faqs.map((faq, i) => (
                <FAQItem key={i} faq={faq} />
              ))}
            </div>
          </section>
        ))}
      </div>
      
      {/* Contact Footer */}
      <div className="glass-card rounded-2xl p-8 text-center bg-primary/5 border border-primary/10 animate-in-card" style={{ animationDelay: "800ms" }}>
        <h3 className="text-lg font-black text-foreground mb-2">Não encontrou o que procurava?</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-lg mx-auto">
          Nosso time de tecnologia está à disposição para auxiliar com dúvidas de engenharia de dados, infraestrutura ou acesso à plataforma.
        </p>
        <button className="px-8 py-3 rounded-xl btn-primary font-bold shadow-lg shadow-primary/20">
          Entrar em contato com o Suporte
        </button>
      </div>

    </div>
  );
}
